import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import lottie, { AnimationItem } from "lottie-web";
import { useDesign } from "../state/designStore";
import { EFFECT_LABELS, PEN_STROKE_WIDTH } from "../types";
import { StickerTimeline } from "./StickerTimeline";

const CANVAS_PX = 320;
const STICKER_UNITS = 512;
const MIN_SCALE = 0.25;
const MAX_SCALE = 4;
const TAP_SLOP_PX = 1.5;
// ---- feel tuning (raise a gain to make that gesture more sensitive) ----
const MOVE_GAIN = 1.3; // text travels 1.3x the finger distance
const PINCH_GAIN = 1.5; // pinch zoom exponent (>1 = faster zoom)
const ROTATE_GAIN = 1.2; // twist rotation multiplier
const SNAP_STEP_DEG = 45; // rotation magnets at 0/45/90...
const SNAP_RANGE_DEG = 4; // how close before it snaps

interface Xf {
  dx: number; // screen px
  dy: number;
  rot: number; // degrees
  s: number; // ratio
}
const IDENTITY: Xf = { dx: 0, dy: 0, rot: 0, s: 1 };

type LottieLayerEl = {
  data?: { nm?: string; ind?: number; parent?: number; ip?: number; op?: number; ks?: { a?: { k?: unknown } } };
  baseElement?: SVGGElement;
  finalTransform?: { mat?: { props?: ArrayLike<number> }; mProp?: { v?: { props?: ArrayLike<number> } } };
};

interface DragLayer {
  g: SVGGElement; // primary wrapper (the instance used for the maths)
  bases: SVGGElement[]; // the layer's own element for each instance (for its bounding box)
  all: SVGGElement[]; // every instance of the layer (a precomp can be placed several times)
  // 2x2 linear part of the parent (motion) layer transform: [a, b, c, d]
  parentLin: [number, number, number, number];
  ip: number;
  op: number;
  inv: DOMMatrix | null; // screen -> layer-parent space
  origin: [number, number]; // layer pivot (its anchor) in composition space
}

export function StickerCanvas() {
  const {
    activeText,
    activeEffect,
    previewJson,
    updateActivePosition,
    design,
    activePen,
    addDrawingStroke,
    removeDrawingStroke,
    undoLastStroke,
    clearDrawings,
    setActivePen,
    isErasing,
    setIsErasing,
    setActiveTextId,
    setActiveEffectId,
    updateLayerTiming,
    updateActiveText,
  } = useDesign();
  const activePosition = activeText?.position ?? activeEffect?.position ?? null;
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  // حجم الكانفس يتكيّف مع العرض المتاح (حتى يتسع الشريط الجانبي)، الأقصى CANVAS_PX
  const wrapRef = useRef<HTMLDivElement>(null);
  const [canvasPx, setCanvasPx] = useState(CANVAS_PX);
  const canvasPxRef = useRef(CANVAS_PX);
  canvasPxRef.current = canvasPx;
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = Math.floor(el.clientWidth);
      if (w > 0) setCanvasPx(Math.max(220, Math.min(CANVAS_PX, w)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [isPlaying, setIsPlaying] = useState(true);
  const isPlayingRef = useRef(true);
  const lastFrameRef = useRef(0);
  const isDrawingToolActive = !!activePen || isErasing;

  const [liveStroke, setLiveStroke] = useState<[number, number][] | null>(null);
  const isDrawing = useRef(false);
  const erasedInThisGesture = useRef<Set<string>>(new Set());

  // ---- hand-drag state (all kept in refs: no React re-render while dragging) ----
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const acc = useRef<Xf>({ ...IDENTITY }); // finished sub-gestures in this session
  const cur = useRef<Xf>({ ...IDENTITY }); // current sub-gesture
  const visBase = useRef<Xf>({ ...IDENTITY }); // already committed but preview not refreshed yet
  const startRef = useRef({ x: 0, y: 0, dist: 0, angle: 0 });
  const movedRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const dragLayerRef = useRef<DragLayer | null>(null);
  const resumeAfterDragRef = useRef(false);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // إعادة تشغيل من لحظة معيّنة (معاينة الرسم): now=true فوراً، وإلا لما توصل المعاينة الجديدة
  const replayRef = useRef<number | null>(null);
  useEffect(() => {
    const onReplay = (e: Event) => {
      const d = (e as CustomEvent<{ fraction?: number; now?: boolean }>).detail || {};
      const f = Math.max(0, Math.min(0.98, d.fraction ?? 0));
      replayRef.current = f;
      setIsPlaying(true);
      isPlayingRef.current = true;
      const anim = animRef.current;
      if (d.now && anim) {
        anim.goToAndPlay(Math.floor((anim.totalFrames || 180) * f), true);
        replayRef.current = null;
      }
    };
    window.addEventListener("sticker:replay", onReplay);
    return () => window.removeEventListener("sticker:replay", onReplay);
  }, []);

  useEffect(() => {
    if (!containerRef.current || !previewJson) return;
    animRef.current?.destroy();
    const anim = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: false,
      animationData: previewJson,
    });
    animRef.current = anim;
    // Fresh preview already contains the committed position, so drop the temporary offset.
    visBase.current = { ...IDENTITY };
    animVersionRef.current++;
    // Keep the playhead where it was so the sticker doesn't restart after every edit.
    let frame = lastFrameRef.current;
    if (replayRef.current !== null) {
      frame = Math.floor((anim.totalFrames || 180) * replayRef.current);
      replayRef.current = null;
    }
    if (isPlayingRef.current) anim.goToAndPlay(frame, true);
    else anim.goToAndStop(frame, true);
    return () => {
      lastFrameRef.current = anim.currentFrame || 0;
      anim.destroy();
      animRef.current = null;
    };
  }, [previewJson]);

  // ---- locate the active text's own layer inside the lottie SVG ----
  // The server names text layers "Name_<text>". They may live in the root
  // composition or inside a precomp asset, so search recursively.
  const findDragLayer = (): DragLayer | null => {
    if (!activeText && !activeEffect) return null;
    const root = (animRef.current as unknown as { renderer?: { elements?: unknown[] } })?.renderer;
    const found: { nm: string; el: LottieLayerEl; siblings: LottieLayerEl[] }[] = [];
    const walk = (list: unknown[] | undefined, depth: number) => {
      if (!list || depth > 6) return;
      for (const raw of list) {
        const el = raw as (LottieLayerEl & { elements?: unknown[] }) | null;
        if (!el) continue;
        const nm = el.data?.nm;
        if (typeof nm === "string" && (nm.startsWith("Name_") || nm.startsWith("Effect_")) && el.baseElement) {
          found.push({ nm, el, siblings: list as LottieLayerEl[] });
        }
        if (el.elements) walk(el.elements, depth + 1);
      }
    };
    walk(root?.elements, 0);
    if (found.length === 0) return null;

    let hits: (typeof found)[number][] = [];
    if (activeText) {
      // Text layers are named "Name_<text>" by the server. A sub-composition that contains the
      // text may be placed more than once in the template, so every instance must move together.
      const want = `Name_${activeText.text}`;
      const texts = found.filter((f) => f.nm.startsWith("Name_"));
      const exact = texts.filter((f) => f.nm === want);
      const trimmed = texts.filter((f) => f.nm.trim() === want.trim());
      hits = exact.length ? exact : trimmed.length ? trimmed : new Set(texts.map((t) => t.nm)).size === 1 ? texts : [];
    } else if (activeEffect && design) {
      // Effect layers are "Effect_<type>". Each new effect is inserted in front of the
      // previous ones, so same-type effects appear in reverse design order.
      const want = `Effect_${activeEffect.effect_type}`;
      const sameLayers = found.filter((f) => f.nm === want);
      const sameDesign = design.effects.filter((e) => e.effect_type === activeEffect.effect_type);
      const i = sameDesign.findIndex((e) => e.id === activeEffect.id);
      if (sameDesign.length === 1) hits = sameLayers;
      else if (sameLayers.length === sameDesign.length && i >= 0) hits = [sameLayers[sameLayers.length - 1 - i]];
    }
    if (hits.length === 0) return null;

    // Wrap every instance (see below) and pick the visible one as the reference.
    const wrappers = hits.map((h) => {
      const base = h.el.baseElement as SVGGElement;
      let wrap = base.parentNode as SVGGElement | null;
      if (!wrap || wrap.getAttribute("data-drag-wrap") !== "1") {
        const created = document.createElementNS("http://www.w3.org/2000/svg", "g");
        created.setAttribute("data-drag-wrap", "1");
        base.parentNode?.insertBefore(created, base);
        created.appendChild(base);
        wrap = created;
      }
      return wrap;
    });
    const visibleIdx = wrappers.findIndex((w) => {
      const r = w.getBoundingClientRect();
      return r.width > 1 && r.height > 1;
    });
    const hit = hits[visibleIdx >= 0 ? visibleIdx : 0];
    const wrap = wrappers[visibleIdx >= 0 ? visibleIdx : 0];

    // The server stores positions in the parent (motion) layer's space. The layer's full matrix
    // is C = P * L (P = parents, L = own local transform), so P = C * L^-1. Both are exposed
    // by lottie per frame.
    let parentLin: [number, number, number, number] = [1, 0, 0, 1];
    const full = hit.el.finalTransform?.mat?.props;
    const local = hit.el.finalTransform?.mProp?.v?.props;
    if (full && local && full.length >= 6 && local.length >= 6) {
      const C = [full[0], full[1], full[4], full[5]]; // a b c d
      const L = [local[0], local[1], local[4], local[5]];
      const detL = L[0] * L[3] - L[1] * L[2];
      if (Math.abs(detL) > 1e-9) {
        const iL = [L[3] / detL, -L[1] / detL, -L[2] / detL, L[0] / detL];
        parentLin = [
          C[0] * iL[0] + C[2] * iL[1],
          C[1] * iL[0] + C[3] * iL[1],
          C[0] * iL[2] + C[2] * iL[3],
          C[1] * iL[2] + C[3] * iL[3],
        ];
      }
    }

    // Pivot the layer scales/rotates around: its anchor point, in composition space.
    let origin: [number, number] = [0, 0];
    if (full && full.length >= 14) {
      const k = hit.el.data?.ks?.a?.k;
      const ax = Array.isArray(k) && typeof k[0] === "number" ? (k[0] as number) : 0;
      const ay = Array.isArray(k) && typeof k[1] === "number" ? (k[1] as number) : 0;
      origin = [full[0] * ax + full[4] * ay + full[12], full[1] * ax + full[5] * ay + full[13]];
    }

    const parentNode = wrap.parentNode as SVGGraphicsElement | null;
    const ctm = parentNode?.getScreenCTM?.() ?? null;
    return {
      g: wrap,
      all: wrappers,
      bases: hits.map((h) => h.el.baseElement as SVGGElement),
      parentLin,
      ip: hit.el.data?.ip ?? 0,
      op: hit.el.data?.op ?? 0,
      inv: ctm ? ctm.inverse() : null,
      origin,
    };
  };

  // screen px -> composition units (what the finger moved, in the sticker's own space)
  const screenToComp = (info: DragLayer, dx: number, dy: number): [number, number] => {
    if (!info.inv) {
      const u = unitsPerScreenPx();
      return [dx * u, dy * u];
    }
    return [info.inv.a * dx + info.inv.c * dy, info.inv.b * dx + info.inv.d * dy];
  };

  // composition-space delta -> the text position delta in parent-layer space
  const compToPosition = (info: DragLayer, dx: number, dy: number): [number, number] => {
    const [a, b, c, d] = info.parentLin;
    const det = a * d - b * c;
    if (Math.abs(det) < 1e-6) return [dx, dy];
    return [(d * dx - c * dy) / det, (-b * dx + a * dy) / det];
  };

  const unitsPerScreenPx = (): number => {
    const svg = containerRef.current?.querySelector("svg");
    const rect = containerRef.current?.getBoundingClientRect();
    const vbw = svg?.viewBox?.baseVal?.width;
    if (svg && rect && rect.width > 0 && vbw) return vbw / rect.width;
    return STICKER_UNITS / canvasPxRef.current;
  };

  // Magnet the final rotation to nice angles so straight text is easy to get.
  const snapRotation = (sessionRot: number): number => {
    const base = activePosition?.rotation ?? 0;
    const abs = base + sessionRot;
    const nearest = Math.round(abs / SNAP_STEP_DEG) * SNAP_STEP_DEG;
    return Math.abs(abs - nearest) <= SNAP_RANGE_DEG ? nearest - base : sessionRot;
  };

  // Keep the live pinch inside the same limits the commit uses (and never NaN/Infinity).
  const clampSessionScale = (ratio: number): number => {
    const base = activePosition?.scale ?? 1;
    const lo = MIN_SCALE / base;
    const hi = MAX_SCALE / base;
    if (!Number.isFinite(ratio)) return 1;
    return Math.max(lo, Math.min(hi, ratio));
  };

  const rafRef = useRef<number | null>(null);
  const scheduleVisual = () => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      applyVisual();
    });
  };

  const applyVisual = () => {
    let info = dragLayerRef.current;
    if (info && !info.g.isConnected) {
      // A fresh server preview replaced the SVG mid-gesture: re-attach to the new layer and
      // freeze it again so the text stays on screen.
      info = findDragLayer();
      dragLayerRef.current = info;
      const anim = animRef.current;
      if (info && anim) anim.goToAndStop(lastFrameRef.current, true);
    }
    if (!info) return;
    const total: Xf = {
      dx: visBase.current.dx + acc.current.dx + cur.current.dx,
      dy: visBase.current.dy + acc.current.dy + cur.current.dy,
      rot: visBase.current.rot + snapRotation(acc.current.rot + cur.current.rot),
      s: visBase.current.s * clampSessionScale(acc.current.s * cur.current.s),
    };
    const [lx, ly] = screenToComp(info, total.dx, total.dy);
    const [ox, oy] = info.origin;
    // Rotate/scale about the layer's own pivot (the same pivot the server uses), then translate.
    const css = `translate(${ox + lx}px, ${oy + ly}px) rotate(${total.rot}deg) scale(${total.s}) translate(${-ox}px, ${-oy}px)`;
    for (const g of info.all) {
      g.style.transformBox = "view-box";
      g.style.transformOrigin = "0 0";
      g.style.transform = css;
    }
  };

  const pointerList = () => Array.from(pointers.current.values());

  // Fold the running sub-gesture into `acc` and re-anchor on the pointers that are down now.
  const rebase = () => {
    acc.current = {
      dx: acc.current.dx + cur.current.dx,
      dy: acc.current.dy + cur.current.dy,
      rot: acc.current.rot + cur.current.rot,
      s: acc.current.s * cur.current.s,
    };
    cur.current = { ...IDENTITY };
    const pts = pointerList();
    if (pts.length === 1) {
      startRef.current = { x: pts[0].x, y: pts[0].y, dist: 0, angle: 0 };
    } else if (pts.length >= 2) {
      const [a, b] = pts;
      startRef.current = {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        dist: Math.max(40, Math.hypot(b.x - a.x, b.y - a.y)),
        angle: Math.atan2(b.y - a.y, b.x - a.x),
      };
    }
  };

  // Freeze on a visible frame, locate the layer(s) and open a gesture session.
  const beginSession = (): boolean => {
    const first = findDragLayer(); // never move the template itself
    if (!first) return false;
    // Freeze the animation on a frame where the text is really on screen (it only exists
    // inside its own time window, and a sub-composition may have its own window too).
    const anim = animRef.current;
    if (anim) {
      resumeAfterDragRef.current = isPlayingRef.current;
      anim.pause();
      const isVisible = () =>
        first.all.some((w) => {
          const r = w.getBoundingClientRect();
          return r.width > 1 && r.height > 1;
        });
      if (!isVisible()) {
        const start = anim.currentFrame || 0;
        const total = anim.totalFrames || 180;
        const lo = Math.max(0, first.ip);
        const hi = first.op > lo ? Math.min(first.op, total) : total;
        const step = Math.max(1, (hi - lo) / 30);
        let ok = false;
        for (let f = lo; f < hi && !ok; f += step) {
          anim.goToAndStop(f, true);
          ok = isVisible();
        }
        if (!ok) anim.goToAndStop(start, true);
      }
    }
    // Geometry (pivot, parent transform, visible reference instance) at the frozen frame.
    const info = findDragLayer();
    if (!info) {
      if (anim && resumeAfterDragRef.current) anim.play();
      return false;
    }
    dragLayerRef.current = info;
    acc.current = { ...IDENTITY };
    cur.current = { ...IDENTITY };
    movedRef.current = false;
    setDragging(true);
    return true;
  };

  const insideBox = (clientX: number, clientY: number): boolean => {
    const rect = containerRef.current?.getBoundingClientRect();
    const pts = (boxRef.current?.getAttribute("points") || "")
      .trim()
      .split(/\s+/)
      .map((t) => t.split(",").map(Number));
    if (!rect || pts.length !== 4 || !pts.every((q) => q.length === 2 && q.every(Number.isFinite))) return false;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const cx = (pts[0][0] + pts[2][0]) / 2;
    const cy = (pts[0][1] + pts[2][1]) / 2;
    // Grow the polygon a little (finger tolerance) and run an even-odd test.
    const g = pts.map(([px, py]) => {
      const L = Math.hypot(px - cx, py - cy) || 1;
      return [px + ((px - cx) / L) * 10, py + ((py - cy) / L) * 10];
    });
    let inside = false;
    for (let i = 0, j = 3; i < 4; j = i++) {
      const [xi, yi] = g[i];
      const [xj, yj] = g[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };

  const handleDragPointerDown = (e: React.PointerEvent) => {
    if (!activePosition) return;
    // Only a touch that lands inside the selection box moves the text; the rest of the canvas is inert.
    if (pointers.current.size === 0 && !insideBox(e.clientX, e.clientY)) return;
    if (pointers.current.size === 0 && !beginSession()) return;
    if (!dragLayerRef.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    rebase();
  };

  // ---- selection box handles: 4 corner circles scale, the pink circle rotates ----
  type HandleKind = "scale" | "rotate";
  const handleRef = useRef<{
    kind: HandleKind;
    cx: number; // box centre on screen: the pivot the finger turns/scales around
    cy: number;
    vx: number; // box centre relative to the layer's anchor (screen px)
    vy: number;
    startAngle: number;
    startDist: number;
  } | null>(null);

  const beginHandle = (kind: HandleKind) => (e: React.PointerEvent) => {
    e.stopPropagation();
    if (!activePosition || handleRef.current) return;
    if (!beginSession()) return;
    const info = dragLayerRef.current;
    if (!info) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const rect = containerRef.current?.getBoundingClientRect();
    // Where the layer's anchor is on screen right now (including what was already dragged).
    const ctm = (info.g.parentNode as SVGGraphicsElement | null)?.getScreenCTM?.();
    const p0 = ctm ? new DOMPoint(info.origin[0], info.origin[1]).matrixTransform(ctm) : null;
    const ax = (p0 ? p0.x : rect ? rect.left + rect.width / 2 : 0) + visBase.current.dx;
    const ay = (p0 ? p0.y : rect ? rect.top + rect.height / 2 : 0) + visBase.current.dy;
    // Centre of the selection box (the polygon is kept in canvas px by the overlay loop).
    let cx = ax;
    let cy = ay;
    const pts = (boxRef.current?.getAttribute("points") || "")
      .trim()
      .split(/\s+/)
      .map((t) => t.split(",").map(Number));
    if (rect && pts.length === 4 && pts.every((q) => q.length === 2 && q.every(Number.isFinite))) {
      cx = rect.left + (pts[0][0] + pts[2][0]) / 2;
      cy = rect.top + (pts[0][1] + pts[2][1]) / 2;
    }
    handleRef.current = {
      kind,
      cx,
      cy,
      vx: cx - ax,
      vy: cy - ay,
      startAngle: Math.atan2(e.clientY - cy, e.clientX - cx),
      startDist: Math.max(24, Math.hypot(e.clientX - cx, e.clientY - cy)),
    };
  };

  const moveHandle = (e: React.PointerEvent) => {
    const h = handleRef.current;
    if (!h) return;
    let rot = 0;
    let sc = 1;
    if (h.kind === "scale") {
      sc = clampSessionScale(Math.hypot(e.clientX - h.cx, e.clientY - h.cy) / h.startDist);
    } else {
      let d = ((Math.atan2(e.clientY - h.cy, e.clientX - h.cx) - h.startAngle) * 180) / Math.PI;
      if (d > 180) d -= 360;
      if (d < -180) d += 360;
      rot = snapRotation(d);
    }
    // Turn/scale about the box centre, not the anchor: the anchor moves by (I - A)·v so the
    // text stays put under the box while the server later renders about the anchor.
    const r = (rot * Math.PI) / 180;
    const cs = Math.cos(r) * sc;
    const sn = Math.sin(r) * sc;
    cur.current = {
      dx: h.vx - (cs * h.vx - sn * h.vy),
      dy: h.vy - (sn * h.vx + cs * h.vy),
      rot,
      s: sc,
    };
    movedRef.current = true;
    scheduleVisual();
  };

  const endHandle = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (!handleRef.current) return;
    handleRef.current = null;
    finishGesture(e.type !== "pointercancel");
  };

  // STRETCH_HANDLES: مد أفقي/عمودي مباشر من مقابض منتصف الأطراف (بمحاور النص نفسها حتى لو مدوّر)
  const stretchRef = useRef<{ axis: "x" | "y"; side: 1 | -1; ux: number; uy: number; cx: number; cy: number; half: number; start: number } | null>(null);
  const [stretchHint, setStretchHint] = useState<{ axis: "x" | "y"; v: number } | null>(null);
  const stretchOnRef = useRef(false);
  stretchOnRef.current = !!activeText && !isDrawingToolActive;

  const beginStretch = (axis: "x" | "y", side: 1 | -1) => (e: React.PointerEvent) => {
    e.stopPropagation();
    if (!activeText || stretchRef.current || handleRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const pts = (boxRef.current?.getAttribute("points") || "")
      .trim()
      .split(/\s+/)
      .map((q) => q.split(",").map(Number));
    if (!rect || pts.length !== 4 || !pts.every((q) => q.length === 2 && q.every(Number.isFinite))) return;
    const [ax, ay] = axis === "x" ? [pts[1][0] - pts[0][0], pts[1][1] - pts[0][1]] : [pts[3][0] - pts[0][0], pts[3][1] - pts[0][1]];
    const len = Math.hypot(ax, ay);
    if (len < 4) return;
    if (!beginSession()) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    stretchRef.current = {
      axis,
      side,
      ux: ax / len,
      uy: ay / len,
      cx: rect.left + (pts[0][0] + pts[2][0]) / 2,
      cy: rect.top + (pts[0][1] + pts[2][1]) / 2,
      half: Math.max(12, len / 2),
      start: axis === "x" ? activeText.stretch_x : activeText.stretch_y,
    };
    setStretchHint({ axis, v: stretchRef.current.start });
  };

  const moveStretch = (e: React.PointerEvent) => {
    const s = stretchRef.current;
    if (!s) return;
    const proj = ((e.clientX - s.cx) * s.ux + (e.clientY - s.cy) * s.uy) * s.side;
    const v = Math.round(Math.max(0.25, Math.min(4, s.start * Math.max(0.1, proj / s.half))) * 100) / 100;
    setStretchHint({ axis: s.axis, v });
    updateActiveText(s.axis === "x" ? { stretch_x: v } : { stretch_y: v });
  };

  const endStretch = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (!stretchRef.current) return;
    stretchRef.current = null;
    setStretchHint(null);
    movedRef.current = false;
    finishGesture(false);
  };

  // ---- selection box overlay (positions are written straight to the DOM, no re-render) ----
  const overlayRef = useRef<SVGSVGElement>(null);
  const boxRef = useRef<SVGPolygonElement>(null);
  const stemRef = useRef<SVGLineElement>(null);
  const handleEls = useRef<(SVGGElement | null)[]>([]);
  const overlayInfoRef = useRef<{ key: string; info: DragLayer | null }>({ key: "", info: null });
  const lastPts = useRef<{ key: string; pts: [number, number][] } | null>(null);
  const animVersionRef = useRef(0);
  const findLayerRef = useRef(findDragLayer);
  findLayerRef.current = findDragLayer;
  const selKey = activeText ? `T${activeText.id}|${activeText.text}` : activeEffect ? `E${activeEffect.id}` : "";
  const overlayOnRef = useRef(false);
  overlayOnRef.current = !!selKey && !isDrawingToolActive;
  const selKeyRef = useRef(selKey);
  selKeyRef.current = selKey;

  useEffect(() => {
    let raf = 0;
    const hide = () => {
      if (overlayRef.current) overlayRef.current.style.visibility = "hidden";
    };
    // مربع التحديد يتحدّث ~30 مرة بالثانية فقط (قبل: كل فريم مع قياسات layout ثقيلة)
    const OVERLAY_MIN_MS = 32;
    let lastRun = 0;
    const tick = (now?: number) => {
      raf = requestAnimationFrame(tick);
      const ts = now ?? performance.now();
      if (ts - lastRun < OVERLAY_MIN_MS) return;
      lastRun = ts;
      const svg = overlayRef.current;
      const canvas = containerRef.current;
      if (!svg || !canvas || !overlayOnRef.current) return hide();
      const key = `${animVersionRef.current}|${selKeyRef.current}`;
      let cached = overlayInfoRef.current;
      if (cached.key !== key || (cached.info && !cached.info.g.isConnected)) {
        cached = { key, info: findLayerRef.current() };
        overlayInfoRef.current = cached;
      }
      const info = cached.info;
      if (!info) return hide();
      let pts: [number, number][] | null = null;
      const i = info.all.findIndex((w) => {
        const r = w.getBoundingClientRect();
        return r.width > 1 && r.height > 1;
      });
      if (i >= 0) {
        try {
          const base = info.bases[i];
          const bb = base.getBBox();
          const M = base.getScreenCTM();
          if (M && bb.width > 0 && bb.height > 0) {
            const rect = canvas.getBoundingClientRect();
            pts = [
              [bb.x, bb.y],
              [bb.x + bb.width, bb.y],
              [bb.x + bb.width, bb.y + bb.height],
              [bb.x, bb.y + bb.height],
            ].map(([x, y]) => {
              const q = new DOMPoint(x, y).matrixTransform(M);
              return [q.x - rect.left, q.y - rect.top] as [number, number];
            });
            lastPts.current = { key, pts };
          }
        } catch {
          /* fall through to last known box */
        }
      }
      // Text can be outside its time window on this frame: keep showing the last known box.
      if (!pts && lastPts.current && lastPts.current.key === key) pts = lastPts.current.pts;
      if (!pts) return hide();
      const clamp = (v: number) => Math.max(12, Math.min(canvasPxRef.current - 12, v));
      boxRef.current?.setAttribute("points", pts.map((q) => q.join(",")).join(" "));
      const ang = (Math.atan2(pts[1][1] - pts[0][1], pts[1][0] - pts[0][0]) * 180) / Math.PI;
      pts.forEach((q, k) => handleEls.current[k]?.setAttribute("transform", `translate(${clamp(q[0])},${clamp(q[1])}) rotate(${ang})`));
      // Rotation handle: out from the middle of the top edge, along its outward normal.
      const cx = (pts[0][0] + pts[2][0]) / 2;
      const cy = (pts[0][1] + pts[2][1]) / 2;
      const tx = (pts[0][0] + pts[1][0]) / 2;
      const ty = (pts[0][1] + pts[1][1]) / 2;
      let nx = tx - cx;
      let ny = ty - cy;
      const len = Math.hypot(nx, ny) || 1;
      nx /= len;
      ny /= len;
      const rx = clamp(tx + nx * 38);
      const ry = clamp(ty + ny * 38);
      handleEls.current[4]?.setAttribute("transform", `translate(${rx},${ry})`);
      stemRef.current?.setAttribute("x1", String(tx));
      stemRef.current?.setAttribute("y1", String(ty));
      stemRef.current?.setAttribute("x2", String(rx));
      stemRef.current?.setAttribute("y2", String(ry));
      // مقابض الأطراف: 5 فوق، 6 يمين، 7 تحت، 8 يسار (تختفي إذا الحافة قصيرة أو الطبقة مو نص)
      const wLen = Math.hypot(pts[1][0] - pts[0][0], pts[1][1] - pts[0][1]);
      const hLen = Math.hypot(pts[3][0] - pts[0][0], pts[3][1] - pts[0][1]);
      const mids: [number, number][] = [
        [(pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2],
        [(pts[1][0] + pts[2][0]) / 2, (pts[1][1] + pts[2][1]) / 2],
        [(pts[2][0] + pts[3][0]) / 2, (pts[2][1] + pts[3][1]) / 2],
        [(pts[3][0] + pts[0][0]) / 2, (pts[3][1] + pts[0][1]) / 2],
      ];
      const lens = [wLen, hLen, wLen, hLen];
      mids.forEach((m, k) => {
        const el = handleEls.current[5 + k];
        if (!el) return;
        const show = stretchOnRef.current && lens[k] >= 32;
        el.style.display = show ? "" : "none";
        if (show) el.setAttribute("transform", `translate(${clamp(m[0])},${clamp(m[1])}) rotate(${ang})`);
      });
      svg.style.visibility = "visible";
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleDragPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = pointerList();
    const st = startRef.current;
    if (pts.length === 1) {
      cur.current = { dx: (pts[0].x - st.x) * MOVE_GAIN, dy: (pts[0].y - st.y) * MOVE_GAIN, rot: 0, s: 1 };
    } else if (pts.length >= 2) {
      const [a, b] = pts;
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      const dist = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      let dAngle = ((Math.atan2(b.y - a.y, b.x - a.x) - st.angle) * 180) / Math.PI;
      if (dAngle > 180) dAngle -= 360;
      if (dAngle < -180) dAngle += 360;
      cur.current = {
        dx: (mx - st.x) * MOVE_GAIN,
        dy: (my - st.y) * MOVE_GAIN,
        rot: dAngle * ROTATE_GAIN,
        s: Math.pow(dist / st.dist, PINCH_GAIN),
      };
    }
    const moved = Math.hypot(acc.current.dx + cur.current.dx, acc.current.dy + cur.current.dy);
    if (moved > TAP_SLOP_PX || pts.length >= 2) movedRef.current = true;
    if (movedRef.current) scheduleVisual();
  };

  const finishGesture = (commit: boolean) => {
    rebase();
    const total = acc.current;
    acc.current = { ...IDENTITY };
    cur.current = { ...IDENTITY };
    setDragging(false);
    const info = dragLayerRef.current;
    const anim = animRef.current;
    if (!commit || !movedRef.current || !activePosition || !info) {
      applyVisualReset();
      if (anim && resumeAfterDragRef.current) anim.play();
      return;
    }
    total.rot = snapRotation(total.rot);
    const [cx, cy] = screenToComp(info, total.dx, total.dy);
    const [px, py] = compToPosition(info, cx, cy);
    const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, activePosition.scale * clampSessionScale(total.s)));
    // Keep the temporary visual offset until the server preview arrives (no snap-back).
    visBase.current = {
      dx: visBase.current.dx + total.dx,
      dy: visBase.current.dy + total.dy,
      rot: visBase.current.rot + total.rot,
      s: visBase.current.s * (nextScale / activePosition.scale),
    };
    updateActivePosition({
      x: activePosition.x + px,
      y: activePosition.y + py,
      rotation: activePosition.rotation + total.rot,
      scale: nextScale,
    });
    // Let the animation keep playing while the server renders: the wrapper keeps the new
    // offset, and the fresh preview resumes from the current frame (see the previewJson effect).
    if (anim && resumeAfterDragRef.current) anim.play();
  };

  // When a gesture is cancelled, restore whatever was already committed.
  const applyVisualReset = () => {
    applyVisual();
  };

  const handleDragPointerUp = (e: React.PointerEvent | PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) finishGesture(e.type !== "pointercancel");
    else rebase();
  };

  // A finger can lift outside the canvas or lose capture; without this the gesture would stay
  // "open" forever and the animation would stay frozen.
  const latestUp = useRef(handleDragPointerUp);
  latestUp.current = handleDragPointerUp;
  useEffect(() => {
    const up = (e: PointerEvent) => latestUp.current(e);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, []);

  const clientToStickerSpace = (e: React.PointerEvent): [number, number] | null => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const u = STICKER_UNITS / canvasPxRef.current;
    return [(e.clientX - rect.left) * u, (e.clientY - rect.top) * u];
  };

  const ERASE_HIT_RADIUS = 18;
  const eraseAtPoint = (pt: [number, number]) => {
    if (!design) return;
    for (const stroke of design.drawings) {
      if (erasedInThisGesture.current.has(stroke.id)) continue;
      const hit = stroke.points.some(([x, y]) => Math.hypot(x - pt[0], y - pt[1]) <= ERASE_HIT_RADIUS);
      if (hit) {
        erasedInThisGesture.current.add(stroke.id);
        removeDrawingStroke(stroke.id);
        break;
      }
    }
  };

  const handleErasePointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    erasedInThisGesture.current = new Set();
    const pt = clientToStickerSpace(e);
    if (pt) eraseAtPoint(pt);
  };

  const handleErasePointerMove = (e: React.PointerEvent) => {
    const pt = clientToStickerSpace(e);
    if (pt) eraseAtPoint(pt);
  };

  const handleDrawPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = clientToStickerSpace(e);
    if (!pt) return;
    isDrawing.current = true;
    setLiveStroke([pt]);
  };

  const handleDrawPointerMove = (e: React.PointerEvent) => {
    if (!isDrawing.current) return;
    const pt = clientToStickerSpace(e);
    if (!pt) return;
    setLiveStroke((prev) => (prev ? [...prev, pt] : [pt]));
  };
  
  const simplifyStroke = (points: [number, number][], tolerance = 1.2): [number, number][] => {
    if (points.length <= 2) return points;
    const perpDist = (p: [number, number], a: [number, number], b: [number, number]) => {
      const [px, py] = p, [ax, ay] = a, [bx, by] = b;
      const dx = bx - ax, dy = by - ay;
      if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);
      const t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
      return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
    };
    const rdp = (pts: [number, number][]): [number, number][] => {
      if (pts.length <= 2) return pts;
      let maxDist = 0;
      let maxIdx = 0;
      for (let i = 1; i < pts.length - 1; i++) {
        const d = perpDist(pts[i], pts[0], pts[pts.length - 1]);
        if (d > maxDist) {
          maxDist = d;
          maxIdx = i;
        }
      }
      if (maxDist <= tolerance) return [pts[0], pts[pts.length - 1]];
      const left = rdp(pts.slice(0, maxIdx + 1));
      const right = rdp(pts.slice(maxIdx));
      return [...left.slice(0, -1), ...right];
    };
    return rdp(points);
  };

  const handleDrawPointerUp = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    setLiveStroke((prev) => {
      if (prev) addDrawingStroke(simplifyStroke(prev));
      return null;
    });
  };

  const pointsToSvgPath = (points: [number, number][]): string =>
    points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

  return (
    <div ref={wrapRef} className="flex flex-col items-center gap-3 w-full">
      <div
        className="relative bg-black/20 rounded-2xl overflow-hidden no-native-drag"
        style={{ width: canvasPx, height: canvasPx }}
      >
        <div ref={containerRef} className="absolute inset-0" />

        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={`0 0 ${STICKER_UNITS} ${STICKER_UNITS}`}
        >
          {design?.drawings.map((stroke) => (
            <path
              key={stroke.id}
              d={pointsToSvgPath(stroke.points)}
              fill="none"
              stroke={stroke.color}
              strokeWidth={PEN_STROKE_WIDTH[stroke.pen_type]}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
          {liveStroke && (
            <path
              d={pointsToSvgPath(liveStroke)}
              fill="none"
              stroke={activePen?.color ?? "#FFFFFF"}
              strokeWidth={activePen ? PEN_STROKE_WIDTH[activePen.penType] : 6}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>

        <div
          className={
            isErasing
              ? "absolute inset-0 cursor-cell"
              : activePen
                ? "absolute inset-0 cursor-crosshair"
                : "absolute inset-0 cursor-grab"
          }
          style={{ touchAction: "none" }}
          onPointerDown={isErasing ? handleErasePointerDown : activePen ? handleDrawPointerDown : handleDragPointerDown}
          onPointerMove={isErasing ? handleErasePointerMove : activePen ? handleDrawPointerMove : handleDragPointerMove}
          onPointerUp={isErasing ? undefined : activePen ? handleDrawPointerUp : handleDragPointerUp}
          onPointerCancel={isErasing ? undefined : activePen ? handleDrawPointerUp : handleDragPointerUp}
          onLostPointerCapture={isErasing || activePen ? undefined : handleDragPointerUp}
        />

        {/* مربع التحديد: إطار رفيع، مربعات الزوايا تكبّر، مقابض الأطراف تمد، والدائرة العلوية تدوّر. */}
        <svg
          ref={overlayRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ visibility: "hidden" }}
          viewBox={`0 0 ${canvasPx} ${canvasPx}`}
        >
          <polygon
            ref={boxRef}
            style={{ stroke: "var(--tg-theme-button-color, #3B82F6)", fill: "rgba(59,130,246,.07)" }}
            strokeWidth={1.6}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <line ref={stemRef} style={{ stroke: "var(--tg-theme-button-color, #3B82F6)" }} strokeWidth={1.4} strokeOpacity={0.7} />
          {[0, 1, 2, 3].map((k) => (
            <g
              key={k}
              ref={(el) => {
                handleEls.current[k] = el;
              }}
              style={{ pointerEvents: "all", touchAction: "none", cursor: "nwse-resize" }}
              onPointerDown={beginHandle("scale")}
              onPointerMove={moveHandle}
              onPointerUp={endHandle}
              onPointerCancel={endHandle}
              onLostPointerCapture={endHandle}
            >
              <circle r={20} fill="transparent" />
              <rect x={-6.5} y={-6.5} width={13} height={13} rx={4} fill="#fff" style={{ stroke: "var(--tg-theme-button-color, #3B82F6)" }} strokeWidth={2.5} />
            </g>
          ))}
          {([
            [5, "y", -1],
            [6, "x", 1],
            [7, "y", 1],
            [8, "x", -1],
          ] as const).map(([idx, axis, side]) => (
            <g
              key={idx}
              ref={(el) => {
                handleEls.current[idx] = el;
              }}
              style={{ pointerEvents: "all", touchAction: "none", cursor: axis === "x" ? "ew-resize" : "ns-resize" }}
              onPointerDown={beginStretch(axis, side)}
              onPointerMove={moveStretch}
              onPointerUp={endStretch}
              onPointerCancel={endStretch}
              onLostPointerCapture={endStretch}
            >
              <circle r={17} fill="transparent" />
              <rect
                x={axis === "x" ? -3.5 : -11}
                y={axis === "x" ? -11 : -3.5}
                width={axis === "x" ? 7 : 22}
                height={axis === "x" ? 22 : 7}
                rx={3.5}
                fill="#fff"
                style={{ stroke: "var(--tg-theme-button-color, #3B82F6)" }}
                strokeWidth={2}
              />
            </g>
          ))}
          <g
            ref={(el) => {
              handleEls.current[4] = el;
            }}
            style={{ pointerEvents: "all", touchAction: "none", cursor: "grab" }}
            onPointerDown={beginHandle("rotate")}
            onPointerMove={moveHandle}
            onPointerUp={endHandle}
            onPointerCancel={endHandle}
            onLostPointerCapture={endHandle}
          >
            <circle r={22} fill="transparent" />
            <circle r={12.5} style={{ fill: "var(--tg-theme-button-color, #3B82F6)" }} stroke="#fff" strokeWidth={2.5} />
            <g transform="translate(-7,-7) scale(.583)" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </g>
          </g>
        </svg>

        {stretchHint && (
          <div
            className="absolute top-2 left-1/2 -translate-x-1/2 px-3 h-8 rounded-full flex items-center gap-1.5 text-xs font-semibold text-white pointer-events-none"
            style={{ background: "rgba(15,23,42,.88)", border: "1px solid rgba(59,130,246,.6)" }}
          >
            <span>{stretchHint.axis === "x" ? "↔ مد أفقي" : "↕ مد عمودي"}</span>
            <span dir="ltr" className="tabular-nums" style={{ color: "#7FB2FF" }}>
              {stretchHint.v.toFixed(2)}×
            </span>
          </div>
        )}

        {isDrawingToolActive && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 no-native-drag">
            <button
              type="button"
              onClick={() => setIsErasing(!isErasing)}
              aria-pressed={isErasing}
              className={`px-3 h-9 rounded-full text-xs font-medium ${
                isErasing ? "bg-tg-button text-tg-buttonText" : "bg-black/60 text-white"
              }`}
            >
              ممحاة
            </button>
            <button
              type="button"
              onClick={undoLastStroke}
              className="px-3 h-9 rounded-full bg-black/60 text-white text-xs font-medium"
            >
              تراجع
            </button>
            <button
              type="button"
              onClick={clearDrawings}
              className="px-3 h-9 rounded-full bg-black/60 text-white text-xs font-medium"
            >
              مسح الكل
            </button>
            <button
              type="button"
              onClick={() => {
                setActivePen(null);
                setIsErasing(false);
              }}
              className="px-4 h-9 rounded-full bg-tg-button text-tg-buttonText text-xs font-semibold"
            >
              تم
            </button>
          </div>
        )}
      </div>

      {!isDrawingToolActive && (
        <p className="text-[11px] text-tg-hint text-center">
          {!activeText && !activeEffect ? "أضف نصاً أو تأثيراً لتتمكن من تحريكه" : dragging ? "اترك الإصبع لتثبيت الموضع" : "اسحب داخل الإطار للتحريك · الزوايا للتكبير · الأطراف للمد · الدائرة للتدوير"}
        </p>
      )}

      {!isDrawingToolActive && previewJson && (
        <StickerTimeline
          animRef={animRef}
          lastFrameRef={lastFrameRef}
          isPlaying={isPlaying}
          setPlaying={setIsPlaying}
          lanes={[
            ...(design?.texts ?? []).map((x) => ({
              key: `t${x.id}`,
              kind: "text" as const,
              id: x.id,
              label: x.text?.trim() || "نص",
              start: x.start_fraction,
              end: x.end_fraction,
              active: activeText?.id === x.id,
            })),
            ...(design?.effects ?? []).map((x) => ({
              key: `e${x.id}`,
              kind: "effect" as const,
              id: x.id,
              label: EFFECT_LABELS[x.effect_type] ?? "تأثير",
              start: x.start_fraction,
              end: x.end_fraction,
              active: activeEffect?.id === x.id,
            })),
          ]}
          onSelect={(l) => (l.kind === "effect" ? setActiveEffectId(l.id) : setActiveTextId(l.id))}
          onLaneTiming={(l, patch) => updateLayerTiming(l.kind, l.id, patch)}
          width={canvasPx}
        />
      )}
    </div>
  );
}
